import '.d{display:flex;}.ssr-plugin'
import '.de{align-items:justify-start;}.ssr-plugin'
import '.df{justify-content:justify-start;}.ssr-plugin'
import '.u{gap:0.5rem;}.ssr-plugin'
import '.a1{cursor:pointer;}.ssr-plugin'
import '.dg{user-select:none;}.ssr-plugin'
import { Style, styled } from 'inlines'
import { Title } from '@app/components/core/typography/Title'
import { Text } from '@app/components/core/typography/Text'
import Column from '@app/components/layout/Column'
import Row from '@app/components/layout/Row'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown, faChevronUp } from '@fortawesome/pro-solid-svg-icons'
import { useState } from 'react'
import { linkIconSizeStyle } from '@app/components/core/link/linkStyleHeper'
import { useTranslations } from '@i18n/useLanguage'
import { Avatar } from '@app/components/core/avatar/Avatar'
import { Badge } from '@app/components/core/badge/Badge'
import { Card } from './Card'
import { Alert } from '@app/components/core/alert/Alert'
import type { StoredImage } from '@app/types/StoredImage'

export interface ItemListCardItemProps {
  image: string | StoredImage
  title: string
  subtitle?: string
  isVerified?: boolean
  onClick?: () => void
}

export interface ItemListCardProps {
  title?: string
  items: ItemListCardItemProps[]
  initialItemsToShow?: number
  style?: Style
}

export const ItemListCard = ({
  title,
  items,
  initialItemsToShow = 3,
  style,
}: ItemListCardProps) => {
  const translations = useTranslations().app.common
  const [isExpanded, setIsExpanded] = useState(false)
  const hasMoreItems = items.length > initialItemsToShow
  const displayedItems = isExpanded ? items : items.slice(0, initialItemsToShow)

  return (
    <Card
      data-testid="item-list-card"
      style={{
        ...style,
      }}
    >
      {/* Optional Title */}
      {title && (
        <Title.H5
          style={{
            marginBottom: '0.5rem',
          }}
        >
          {title}
        </Title.H5>
      )}

      {/* List of Tiles */}
      <Column gap="1rem" alignItems="stretch">
        {displayedItems.length ? (
          displayedItems.map((item, index) => (
            <ItemListCardItem key={index} item={item} />
          ))
        ) : (
          <Alert variant="info" size="small">
            {translations.noItems}
          </Alert>
        )}
      </Column>

      {/* Show More/Less Toggle */}
      {hasMoreItems && (
        <div className="d de df u a1 dg"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <Text.Subtitle
            style={{
              color: 'var(--brand-secondary)',
              fontWeight: '500',
            }}
          >
            {isExpanded ? translations.hide : translations.seeMore}
          </Text.Subtitle>
          <FontAwesomeIcon
            icon={isExpanded ? faChevronUp : faChevronDown}
            style={{
              color: 'var(--brand-secondary)',
              height: '1rem',
              width: '1rem',
            }}
          />
        </div>
      )}
    </Card>
  )
}

export const ItemListCardItem = ({ item }: { item: ItemListCardItemProps }) => {
  return (
    <Row
      gap="0.75rem"
      style={{
        alignItems: 'center',
        cursor: item.onClick ? 'pointer' : 'default',
      }}
      onClick={item.onClick}
    >
      <Avatar shape="square" image={item.image} labelSource={item.title} />
      <Column gap="0.25rem" style={{ flex: 1 }}>
        <Row
          style={{
            gap: '0.5rem',
            alignItems: 'center',
          }}
        >
          <Title.H5
            style={{
              color: 'var(--gray-900)',
              margin: 0,
            }}
          >
            {item.title}
          </Title.H5>
          {item.isVerified && <Badge style={linkIconSizeStyle['medium']} />}
        </Row>

        {item.subtitle && (
          <Text.Small
            style={{
              color: 'var(--gray-900)',
            }}
          >
            {item.subtitle}
          </Text.Small>
        )}
      </Column>
    </Row>
  )
}
